import { OperationModels } from '../../../kernel/base-entities'
import { CurrencyCode, PaymentDetails } from './job-nebula'

export enum TaskNames {
  DELIVERY = 'DELIVERY',
  PICKING = 'PICKING',
  PICK_UP = 'PICK_UP',
  ZONE_PICKING = 'ZONE_PICKING',
  PICKING_WITH_STORAGE = 'PICKING_WITH_STORAGE',
  PICKING_AND_STORAGE = 'PICKING_AND_STORAGE',
  CONSOLIDATION = 'CONSOLIDATION',
  DELIVERY_WITH_STORAGE = 'DELIVERY_WITH_STORAGE',
  PICK_UP_FOR_DELIVERY = 'PICK_UP_FOR_DELIVERY',
  FULL_SERVICE = 'FULL_SERVICE',
  STORAGE = 'STORAGE',
  PICKING_MULTI_STOP = 'PICKING_MULTI_STOP',
  PICKING_VERIFICATION = 'PICKING_VERIFICATION'
}

export enum StepTypeNames {
  CHECKING_OUT = 'CHECKING_OUT',
  TRANSFERRING_FROM = 'TRANSFERRING_FROM',
  TRANSFERRING_FROM_STORAGE_TO_DELIVERY = 'TRANSFERRING_FROM_STORAGE_TO_DELIVERY',
  TRANSFERRING_TO_DELIVERY_FROM_STORAGE = 'TRANSFERRING_TO_DELIVERY_FROM_STORAGE',
  TRANSFERRING_FROM_PICKING_TO_STORAGE = 'TRANSFERRING_FROM_PICKING_TO_STORAGE',
  DELIVERING = 'DELIVERING',
  PARKING = 'PARKING',
  GOING_TO_ORIGIN = 'GOING_TO_ORIGIN',
  STORING = 'STORING',
  TRANSFERRING_TO = 'TRANSFERRING_TO',
  PICKING = 'PICKING',
  TRANSFERRING_TO_STORAGE_FROM_PICKING = 'TRANSFERRING_TO_STORAGE_FROM_PICKING',
  GOING_TO_DESTINATION = 'GOING_TO_DESTINATION',
  EXECUTION_DONE = 'EXECUTION_DONE',
  AUDITING = 'AUDITING',
  PICKING_WITH_PACKING = 'PICKING_WITH_PACKING',
  FINISHED = 'FINISHED',
  BACK_TO_STORE = 'BACK_TO_STORE',
  VERIFICATION = 'VERIFICATION'
}

export enum TaskState {
  PROCESSING = 'PROCESSING',
  ASSIGNED = 'ASSIGNED',
  DOING = 'DOING',
  FINISHED = 'FINISHED',
  CANCELED = 'CANCELED',
  WAITING = 'WAITING'
}

export enum StepState {
  PENDING = 'PENDING',
  DOING = 'DOING',
  DONE = 'DONE'
}

export type TaskName = keyof typeof TaskNames
export type StepName = keyof typeof StepTypeNames

export type Step = {
  name?: string
  clientId?: number
  actualEnd: string | null
  actualStart: string
  createdAt?: string
  hasOptimalFinishFlag?: boolean
  hasOptimalStartFlag?: boolean
  id?: number
  jobStatus?: string
  jobUUID?: string
  odinClientId?: number
  optimalFinish: string
  optimalStart: string
  ordering: number
  status: StepState
  stepTypeId?: number
  taskId?: number
  taskStatus?: TaskState
  type: {
    createdAt?: string
    id?: number
    name: StepName
    odinClientId?: number
    optimalFinishFlag?: boolean
    optimalStartFlag?: boolean
    updatedAt?: string
  }
  updatedAt?: string
  uuid: string
  active: boolean | null
}

export type Task = {
  clientId?: number
  confirmPayment?: boolean
  createdAt?: string
  departments: any[]
  fleet?: string
  id?: number
  jobId?: number
  name: TaskName
  optimalAssignationTime?: string
  optimalEndTime?: string
  optimalStartTime?: string
  payment?: { currencyCode: CurrencyCode; details: PaymentDetails; total: number }
  realEndTime?: string | null
  realStartTime?: string
  resources: TaskResource | []
  route?: { id: string; ordering: number; routeNumber: string }
  status: TaskState
  steps?: Step[]
  updatedAt?: string
  uuid: string
  storeId?: string | null
  stopMetadata?: TaskStopMetada | null
}

export type TaskRoute = {
  id: string
  ordering: number
  routeNumber: string
}

export type TaskResource = {
  firstName: string
  lastName?: string
  phoneNumber: string
  photoUrl: string
  fleetId?: string
  resourceId: string
}

export type TaskPayment = {
  currencyCode: string
  details: {
    base: number
    onTime: number
    orderCompleted: number
  }
  total: number
}

export interface TaskStopMetada {
  order: number
  storeName: string
}

const getTaskByType = (type: TaskNames, tasks: Task[]): Task => {
  const results = tasks.filter((taskInArray) => {
    return taskInArray.name === type
  })
  if (results.length === 0) {
    throw new Error('TASK_OF_TYPE_NOT_PRESENT_IN_JOB')
  }
  if (results.length > 1) {
    throw new Error(`MULTIPLE_TASKS_OF_TYPE_${type}_PRESENT_IN_JOB`)
  }
  return results[0]
}

const pickingTask: Record<OperationModels, (tasks: Task[]) => Task> = {
  [OperationModels.FULL_SERVICE]: (tasks) => getTaskByType(TaskNames.FULL_SERVICE, tasks),
  [OperationModels.PICK_AND_COLLECT]: (tasks) => getTaskByType(TaskNames.PICKING_WITH_STORAGE, tasks),
  [OperationModels.PICK_AND_DELIVERY]: (tasks) => getTaskByType(TaskNames.PICKING, tasks),
  [OperationModels.PICK_AND_DELIVERY_WITH_STORAGE]: (tasks) => getTaskByType(TaskNames.PICKING_WITH_STORAGE, tasks),
  [OperationModels.PICK_AND_COLLECT_NO_TRANSFER]: (tasks) => getTaskByType(TaskNames.PICKING_AND_STORAGE, tasks),
  [OperationModels.PICK_AND_DELIVERY_WITH_STORAGE_NO_TRANSFER]: (tasks) =>
    getTaskByType(TaskNames.PICKING_AND_STORAGE, tasks),
  [OperationModels.ZONE_PICKING_AND_DELIVERY_WITH_STORAGE]: (tasks) => getTaskByType(TaskNames.CONSOLIDATION, tasks),
  [OperationModels.ZONE_PICKING_AND_COLLECT]: (tasks) => getTaskByType(TaskNames.CONSOLIDATION, tasks)
}

export const getPickingTask = (operationModel: OperationModels, tasks: Task[]): Task => {
  const mapper = pickingTask[operationModel]

  if (!mapper) {
    throw new Error(`OPERATIONAL_MODEL_${operationModel}_DOES_NOT_SUPPORT_PICKING_TASKS`)
  }

  return mapper(tasks)
}

export const getStepOfType = (stepType: StepTypeNames, steps: Step[]): Step => {
  const targetStep = steps.find((step) => {
    return step.type.name === stepType
  })
  if (!targetStep) {
    throw new Error('STEP_FOR_TASK_NOT_PRESENT')
  }
  return targetStep
}
